import React, { useState } from "react";

import PageTitle from "../../../layouts/PageTitle";
import { appApi, useGetNcsNewConnectionsQuery } from "../../../../store/appApi";
import { useDispatch } from "react-redux";
import {
  usePartnerCode,
  useProjectPartners,
  panelClass,
  fieldClass,
  btnGhost,
  Badge,
  STATUS_TONE,
  fmtDate,
  selectWhiteClass,
  WHITE_CONTROL_STYLE,
  WHITE_OPTION_STYLE,
} from "./config";
import CustomerDrawer from "./CustomerDrawer";
import {
  downloadWorkbook,
  addCustomerNoteApi,
} from "../../../../services/netplusCustomerStatus";
import { uiToast } from "./ui";

const errMsg = (e) => e?.response?.data?.message || "Something went wrong.";
// Filters/inputs on this sheet must stay white-on-dark-text even in dark mode.
const WHITE_FIELD =
  "rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-800 outline-none";
const WHITE_FIELD_STYLE = { backgroundColor: "#fff", color: "#1f2937" };

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const Opt = ({ value, children }) => (
  <option value={value} style={WHITE_OPTION_STYLE}>
    {children}
  </option>
);

const NcsNewConnections = () => {
  const dispatch = useDispatch();
  const [partnerCode, setPartnerCode] = usePartnerCode();
  const { codes: allPartnerCodes } = useProjectPartners();
  // Local to this screen only — never written to the shared, cross-page
  // selected partner (usePartnerCode/localStorage).
  const [viewAll, setViewAll] = useState(false);
  const now = new Date();
  const [year, setYear] = useState(String(now.getFullYear()));
  const [month, setMonth] = useState(String(now.getMonth() + 1));
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [q, setQ] = useState("");
  const [qLive, setQLive] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [noteDrafts, setNoteDrafts] = useState({});
  const [savingNote, setSavingNote] = useState("");

  const { data, isFetching } = useGetNcsNewConnectionsQuery({
    partnerCode: viewAll ? "ALL" : partnerCode,
    year,
    month,
    from: fromDate,
    to: toDate,
    q: qLive,
    page,
    limit: 50,
  });

  const rows = data?.customers || [];
  const totalPages = data?.totalPages || 1;
  const total = data?.totalCount ?? 0;

  const getNoteDraft = (key) => noteDrafts[key] || { open: false, text: "" };
  const setNoteDraft = (key, patch) =>
    setNoteDrafts((prev) => ({ ...prev, [key]: { ...getNoteDraft(key), ...patch } }));
  const toggleNote = (key) => setNoteDraft(key, { open: !getNoteDraft(key).open });

  const saveNote = async (c) => {
    const draft = getNoteDraft(c.customerKey);
    if (!draft.text.trim()) return;
    try {
      setSavingNote(c.customerKey);
      await addCustomerNoteApi(c.customerKey, draft.text.trim(), c.partnerCode || partnerCode);
      setNoteDrafts((prev) => ({ ...prev, [c.customerKey]: { open: false, text: "" } }));
      dispatch(appApi.util.invalidateTags(["NetplusCustomerStatus"]));
    } catch (e) {
      uiToast({ type: "error", title: "Couldn't save remark", message: errMsg(e) });
    } finally {
      setSavingNote("");
    }
  };

  // Year options — real years from the API, plus the current one, newest first.
  const years = Array.from(
    new Set([now.getFullYear(), ...(data?.years || [])].map(String)),
  ).sort((a, b) => Number(b) - Number(a));

  const applySearch = (e) => {
    e.preventDefault();
    setQLive(q.trim());
    setPage(1);
  };

  const resetPage = (fn) => (v) => {
    fn(v);
    setPage(1);
  };

  return (
    <>
      <PageTitle activeMenu="New Connections" motherMenu="Netplus" />

      <section className={panelClass}>
        <div className="flex flex-wrap items-end gap-3 border-b border-gray-100 px-5 py-3">
          <h2 className="mr-auto text-sm font-semibold uppercase tracking-wide text-gray-500">
            New connections
            <span className="ml-2 rounded-full bg-cyan-50 px-2 py-0.5 text-xs font-semibold text-cyan-700">
              {total} in {data?.period || "—"}
            </span>
          </h2>

          <div>
            <span className="mb-1 block text-[11px] uppercase tracking-wide text-gray-400">
              Year
            </span>
            <select
              className={selectWhiteClass}
              style={WHITE_CONTROL_STYLE}
              value={year}
              onChange={(e) => resetPage(setYear)(e.target.value)}
            >
              <Opt value="">All years</Opt>
              {years.map((y) => (
                <Opt key={y} value={y}>
                  {y}
                </Opt>
              ))}
            </select>
          </div>

          <div>
            <span className="mb-1 block text-[11px] uppercase tracking-wide text-gray-400">
              Month
            </span>
            <select
              className={selectWhiteClass}
              style={WHITE_CONTROL_STYLE}
              value={month}
              onChange={(e) => resetPage(setMonth)(e.target.value)}
              disabled={!year}
            >
              <Opt value="">All months</Opt>
              {MONTHS.map((m, i) => (
                <Opt key={m} value={i + 1}>
                  {m}
                </Opt>
              ))}
            </select>
          </div>

          <div>
            <span className="mb-1 block text-[11px] uppercase tracking-wide text-gray-400">
              From
            </span>
            <input
              type="date"
              className={fieldClass}
              value={fromDate}
              onChange={(e) => resetPage(setFromDate)(e.target.value)}
            />
          </div>
          <div>
            <span className="mb-1 block text-[11px] uppercase tracking-wide text-gray-400">
              To
            </span>
            <input
              type="date"
              className={fieldClass}
              value={toDate}
              onChange={(e) => resetPage(setToDate)(e.target.value)}
            />
          </div>
          {(fromDate || toDate) && (
            <button
              type="button"
              className={btnGhost}
              onClick={() => {
                setFromDate("");
                setToDate("");
                setPage(1);
              }}
            >
              Clear dates
            </button>
          )}

          <form onSubmit={applySearch} className="flex items-center gap-2">
            <input
              className={`${fieldClass} w-52`}
              placeholder="Name / phone / id"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <button type="submit" className={btnGhost}>
              Search
            </button>
          </form>

          <label
            className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border border-gray-300 bg-white px-2 py-1"
            style={{ backgroundColor: "#fff" }}
            title="Partner code"
          >
            <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
              Partner
            </span>
            <select
              className="bg-white text-xs font-semibold text-gray-800 outline-none"
              style={{ backgroundColor: "#fff", color: "#1f2937" }}
              value={viewAll ? "ALL" : partnerCode}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "ALL") {
                  setViewAll(true);
                } else {
                  setViewAll(false);
                  setPartnerCode(v);
                }
                setPage(1);
              }}
            >
              <option value="ALL" style={{ color: "#1f2937", backgroundColor: "#fff" }}>
                All partners
              </option>
              {(allPartnerCodes.length ? allPartnerCodes : [partnerCode]).map((c) => (
                <option key={c} value={c} style={{ color: "#1f2937", backgroundColor: "#fff" }}>
                  {c}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className={btnGhost}
            onClick={() => downloadWorkbook(partnerCode, { view: "customers" })}
          >
            Export
          </button>
        </div>
        <p className="border-b border-gray-100 px-5 py-2 text-[11px] text-gray-400">
          Counted here once the sheet's Created date and First Activation date land on the{" "}
          <span className="font-medium text-emerald-600">same day</span> — until Activation
          Date is included in the upload, Created date alone still qualifies.
        </p>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                {viewAll && <th className="px-4 py-3 text-left">Partner</th>}
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-left">User Id</th>
                <th className="px-4 py-3 text-left">Phone</th>
                <th className="px-4 py-3 text-center">Status</th>
                <th className="px-4 py-3 text-center">Created</th>
                <th className="px-4 py-3 text-center">First Activation</th>
                <th className="px-4 py-3 text-center">Expiry</th>
                <th className="px-4 py-3 text-left">Service area</th>
                <th className="px-4 py-3 text-left">Remarks</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isFetching ? (
                <tr>
                  <td colSpan={9 + (viewAll ? 1 : 0)} className="px-4 py-12 text-center text-gray-400">
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={9 + (viewAll ? 1 : 0)} className="px-4 py-12 text-center text-gray-400">
                    No new connections for {viewAll ? "any partner" : partnerCode} in{" "}
                    {data?.period || "this period"}.
                  </td>
                </tr>
              ) : (
                rows.map((c) => (
                  <tr
                    key={c._id}
                    onClick={() =>
                      setSelected({ customerKey: c.customerKey, partnerCode: c.partnerCode })
                    }
                    className="cursor-pointer hover:bg-cyan-50/40"
                  >
                    {viewAll && (
                      <td className="px-4 py-2.5 text-xs font-semibold text-gray-500">
                        {c.partnerCode}
                      </td>
                    )}
                    <td className="px-4 py-2.5 font-medium text-gray-800">
                      {c.name || "—"}
                    </td>
                    <td className="px-4 py-2.5 text-gray-500">{c.userId}</td>
                    <td className="px-4 py-2.5 text-gray-500">{c.phone || "—"}</td>
                    <td className="px-4 py-2.5 text-center">
                      <Badge tone={STATUS_TONE[c.status]}>{c.status}</Badge>
                    </td>
                    <td className="px-4 py-2.5 text-center font-medium text-gray-700">
                      {fmtDate(c.createDate)}
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      {c.firstActivationDate ? (
                        <span
                          className={
                            fmtDate(c.firstActivationDate) === fmtDate(c.createDate)
                              ? "font-medium text-emerald-600"
                              : "text-gray-500"
                          }
                        >
                          {fmtDate(c.firstActivationDate)}
                        </span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-center text-gray-500">
                      {fmtDate(c.expiryDate)}
                    </td>
                    <td className="px-4 py-2.5 text-gray-500">
                      {c.serviceArea || "—"}
                    </td>
                    <td
                      className="px-4 py-2.5"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {getNoteDraft(c.customerKey).open ? (
                        <div className="flex items-center gap-1.5">
                          <input
                            autoFocus
                            className={`${WHITE_FIELD} w-40`}
                            style={WHITE_FIELD_STYLE}
                            placeholder="Add a remark…"
                            value={getNoteDraft(c.customerKey).text}
                            onChange={(e) =>
                              setNoteDraft(c.customerKey, { text: e.target.value })
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") saveNote(c);
                              if (e.key === "Escape") toggleNote(c.customerKey);
                            }}
                          />
                          <button
                            className="rounded-lg bg-cyan-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-cyan-500 disabled:opacity-50"
                            disabled={savingNote === c.customerKey}
                            onClick={() => saveNote(c)}
                          >
                            {savingNote === c.customerKey ? "…" : "Save"}
                          </button>
                        </div>
                      ) : (
                        <button
                          className="flex max-w-[11rem] items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 py-1 text-left text-xs text-gray-600 transition hover:border-cyan-400 hover:text-cyan-700"
                          onClick={() => toggleNote(c.customerKey)}
                        >
                          {c.lastNote ? (
                            <span className="truncate text-blue-700">
                              {c.lastNote.text}
                            </span>
                          ) : (
                            <span className="text-gray-400">+ Remark</span>
                          )}
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-5 py-3 text-xs text-gray-500">
          <span>{total} total</span>
          <div className="flex items-center gap-2">
            <button
              className={btnGhost}
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Prev
            </button>
            <span>
              {page} / {totalPages}
            </span>
            <button
              className={btnGhost}
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </section>

      <CustomerDrawer
        partnerCode={selected?.partnerCode || partnerCode}
        customerKey={selected?.customerKey}
        onClose={() => setSelected(null)}
      />
    </>
  );
};

export default NcsNewConnections;
